import Button from '@mui/material/Button';
import type { ButtonBaseProps } from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import type { TooltipProps } from '@mui/material/Tooltip';
import Tooltip from '@mui/material/Tooltip';
import type { ComponentProps, KeyboardEventHandler, ReactNode } from 'react';
import { createContext, useCallback, useId, useMemo, useState } from 'react';

import { ArrowDown, Overflow } from '../../icons';

export const ContextMenuCloseContext = createContext<() => void>(() => {});

type ContextMenuAccessibleName =
    | {
          label: string;
          tooltip?: string;
      }
    | {
          label?: string;
          tooltip: string;
      };

export type ContextMenuProps = ContextMenuAccessibleName & {
    icon?: ReactNode;

    tooltipPlacement?: TooltipProps['placement'];

    disabled?: ComponentProps<typeof Button>['disabled'];

    onClose?: () => void;

    children?: ReactNode;
};

export function ContextMenu({
    icon = <Overflow fontSize="small" />,
    label,
    tooltip,
    tooltipPlacement = 'top',
    disabled,
    children,
    onClose,
}: Readonly<ContextMenuProps>) {
    const menuId = useId();
    const buttonId = useId();

    const [buttonEl, setButtonEl] = useState<HTMLButtonElement | null>(null);
    const [open, setOpen] = useState(false);

    const handleClose = useCallback(() => {
        setOpen(false);
        onClose?.();
    }, [onClose]);

    const handleButtonKeyDown: KeyboardEventHandler = useCallback((event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
        }
    }, []);

    const triggerProps: ButtonBaseProps<typeof Button> = useMemo(
        () => ({
            id: buttonId,
            ref: setButtonEl,

            onClick: () => setOpen((current) => !current),
            onKeyDown: handleButtonKeyDown,

            ['aria-label']: tooltip,
            ['aria-controls']: open ? menuId : undefined,
            ['aria-haspopup']: 'menu',
            ['aria-expanded']: open,
        }),
        [buttonId, handleButtonKeyDown, menuId, open, tooltip],
    );

    const trigger = label ? (
        <Button
            {...triggerProps}
            color="primary"
            variant="contained"
            endIcon={<ArrowDown />}
            disabled={disabled}
        >
            {label}
        </Button>
    ) : (
        <IconButton {...triggerProps} size="small" disabled={disabled}>
            {icon}
        </IconButton>
    );

    return (
        <>
            <Tooltip title={tooltip} placement={tooltipPlacement}>
                {disabled ? <span>{trigger}</span> : trigger}
            </Tooltip>

            <Menu
                id={menuId}
                anchorEl={buttonEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    list: {
                        dense: true,
                        'aria-labelledby': buttonId,
                        autoFocus: true,
                        autoFocusItem: true,
                    },
                    paper: {
                        elevation: 8,
                    },
                }}
            >
                <ContextMenuCloseContext.Provider value={handleClose}>
                    {children}
                </ContextMenuCloseContext.Provider>
            </Menu>
        </>
    );
}
